import * as React from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import { Variable } from '@restworld/utility-types';

import Checkbox, { CheckBoxVariantType, ICheckboxProps } from './checkbox.basic';
import { Heading } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

import type { JSX } from 'react';

export const CheckboxGroup: React.FunctionComponent<ICheckboxGroupProps> = ({
  id: checkBoxId,
  options,
  isWrapList = true,
  value,
  mode = 'static',
  variant = 'circle',
  helperText,
  isOrientation,
  mainTitle,
  subTitle,
  wrapperStyles,
  isEditable = false,
  testId,
  ...rest
}) => {
  return options?.length ? (
    <div className="flex flex-col gap-4 w-full">
      {!!mainTitle && (
        <Heading size={4} weight="semibold" align="center">
          {mainTitle}
        </Heading>
      )}
      {!!subTitle && (
        <Heading size={6} weight="normal" color="muted" align="center">
          {subTitle}
        </Heading>
      )}
      <Wrapper
        wrapperStyles={wrapperStyles}
        isOrientation={isOrientation}
        id="checkbox"
        isWrapList={isWrapList}
        variant={variant}
        isEditable={isEditable}
        data-cy={testId}
      >
        {options.map(({ key, label, id }) => (
          <Item id={`${id}-${checkBoxId}`} key={key} isEditable={isEditable}>
            <Checkbox
              key={key}
              title={label}
              isChecked={value ? value.indexOf(id as never) !== -1 : undefined}
              variant={variant}
              mode={mode}
              id={id?.toString()}
              testId={`${key}-checkbox`}
              {...rest}
            />
          </Item>
        ))}
      </Wrapper>
      {helperText}
    </div>
  ) : null;
};
export default CheckboxGroup;

const Wrapper = ({
  isWrapList,
  isOrientation,
  variant,
  isEditable,
  wrapperStyles,
  className,
  children,
  ...props
}: {
  isWrapList?: boolean;
  isOrientation?: string;
  variant?: CheckBoxVariantType;
  isEditable?: boolean;
  wrapperStyles?: string;
  className?: string;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLUListElement>) => (
  <ul
    className={cn(
      'flex gap-2 h-full md:max-h-[15rem] md:overflow-y-auto md:px-4 md:pt-1',
      isWrapList ? 'md:flex-wrap' : '',
      isOrientation === 'row' ? 'flex-row gap-4 flex-wrap' : 'flex-col',
      variant === 'chip' ? 'w-full flex-row flex-wrap' : '',
      isEditable ? '!px-0 md:max-h-fit' : '',
      wrapperStyles,
      className
    )}
    {...props}
  >
    {children}
  </ul>
);

const Item = ({
  isEditable,
  className,
  children,
  ...props
}: { isEditable?: boolean; className?: string; children?: React.ReactNode } & React.HTMLAttributes<HTMLLIElement>) => (
  <li className={cn('pb-2 pl-1', isEditable ? '!pl-0' : '', className)} {...props}>
    {children}
  </li>
);

export interface ICheckboxGroupProps extends Omit<ICheckboxProps, 'value'> {
  options?: Variable[];
  value?: string[] | number[];
  isWrapList?: boolean;
  helperText?: React.ReactNode;
  color?: string;
  setValue?: UseFormSetValue<FieldValues>;
  isOrientation?: string;
  mainTitle?: string | JSX.Element;
  subTitle?: string;
  wrapperStyles?: string;
  isEditable?: boolean;
}
